import { Injectable, Optional } from '@angular/core';
import { FilesService } from '@careapp-group/ng-sdk';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { isBefore, parseISO } from 'date-fns';
import { of } from 'rxjs';
import { catchError, filter, map, mergeMap, switchMap, withLatestFrom } from 'rxjs/operators';
import { FilesSelectors } from './files-selectors';
import * as actions from './files.actions';
import { RollbarService } from 'angular-rollbar';

@Injectable()
export class FilesEffects {
  getDownloadUrl$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.requestDownloadUrl),
      withLatestFrom(this.store.select(FilesSelectors.getFilesState)),
      filter(([action, filesState]) => !filesState.loadingIds[action.id]),
      filter(
        ([action, filesState]) =>
          !(
            filesState.urls[action.id]?.expiresAt ||
            isBefore(parseISO(filesState.urls[action.id]?.expiresAt), Date.now())
          ) ?? true
      ),
      map(([action, _]) => actions.requestFetchDownloadUrl({ id: action.id }))
    )
  );

  fetchDownloadUrl$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.requestFetchDownloadUrl),
      withLatestFrom(this.store.select(FilesSelectors.getFilesState)),
      mergeMap(([action, _]) => {
        return this.filesApi.getDownloadUrl({ id: action.id }).pipe(
          map((payload) =>
            actions.responseSuccessDownloadUrl({
              id: action.id,
              url: payload,
            })
          ),
          catchError((err) => {
            this.errorLogger?.error(err);
            return of(actions.responseFailDownloadUrl({ error: err, id: action.id }));
          })
        );
      })
    )
  );
  logFileView$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.requestLogFileView),
      switchMap((action) => {
        return this.filesApi.logFileAccess({ id: action.fileId }).pipe(
          map((payload) => actions.successLogFileView({ fileId: action.fileId })),
          catchError((err) => {
            this.errorLogger?.error(err);
            return of(actions.failLogFileView({ error: err, fileId: action.fileId }));
          })
        );
      })
    )
  );
  fetchFileHistory$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.requestFileHistory),
      switchMap((action) => {
        return this.filesApi.getFileHistory({ id: action.fileId }).pipe(
          map((payload) => actions.successFileHistory({ fileId: action.fileId, history: payload })),
          catchError((err) => {
            this.errorLogger?.error(err);
            return of(actions.failFileHistory({ error: err, fileId: action.fileId }));
          })
        );
      })
    )
  );

  constructor(
    private store: Store<any>,
    private actions$: Actions<any>,
    private filesApi: FilesService,
    @Optional() private errorLogger?: RollbarService
  ) {}
}
